/**
 * useUserGroups + user-group management hooks — extracted from
 * `use-users.ts` per issue #134 (split monolithic Users hook).
 *
 * Covers:
 *   - Per-user group memberships (read): useUserGroupMemberships
 *   - Group list: useUserGroups, UserGroupRow
 *   - Group detail: useUserGroup, UserGroupDetail, UserGroupMember,
 *     UserGroupPermission
 *   - Group CRUD: useCreateUserGroup, useUpdateUserGroup,
 *     useDeleteUserGroup
 *   - Group membership: useAddGroupMembers, useRemoveGroupMember
 *
 * The shapes + behaviour are unchanged from the pre-split version. All
 * consumers continue to import from `lib/use-users` (which re-exports
 * everything here) — direct imports from `lib/use-user-groups` are also
 * supported for new code.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from './api-client';

// ─── Per-user group memberships (read) — was in use-users.ts ───────────────

export interface UserGroupMembership {
  groupId: string;
  name: string;
  description: string | null;
  membershipType: string;
  addedAt: string;
  addedBy: { id: string | null; email: string | null } | null;
  memberCount: number;
}

/**
 * Fetch the actual group memberships for one user. Backed by the new
 * `GET /users/:id/groups` endpoint (commit ad4b7f4 — closes prior audit
 * CM-4). Returns one row per group the user is in, with the joined
 * group name + description so the UI can render the list without an
 * extra catalog round-trip.
 */
export function useUserGroupMemberships(userId: string | undefined): {
  groups: UserGroupMembership[];
  isLoading: boolean;
  error: unknown;
} {
  const { data, isLoading, error } = useQuery<{ data: UserGroupMembership[] }>({
    queryKey: ['users', userId, 'groups'],
    queryFn: () =>
      apiClient.get<{ data: UserGroupMembership[] }>(`/users/${userId}/groups`),
    enabled: !!userId,
    staleTime: 30_000,
  });
  return {
    groups: data?.data ?? [],
    isLoading,
    error,
  };
}

// ─── W1a-09 — User Groups list ─────────────────────────────────────────────

/**
 * Wire-shape for a single row from `GET /api/v1/users/groups`.
 *
 * Groups are tenant-scoped named user bundles for permission grants. v1
 * supports `membershipType='manual'` only — admin curates members
 * explicitly via W1a-10's member-management endpoints. The `'dynamic'`
 * membership type (predicate-based, query-driven) is scaffolded in the
 * schema but rejected at create time (F-W1a-09.1, lands in v1.1).
 *
 * `memberCount` is computed server-side via a correlated COUNT subquery
 * against `user_group_memberships`. `createdBy` is denormalized from the
 * user's identity columns so the table can render "Created by First
 * Last" without a second round-trip.
 *
 * Naming note: hooks below use `useUserGroups…` (not `useGroups…`) to
 * leave the `useGroups` namespace open for Wave 5's Catalog Groups
 * (per `docs/catalog-groups-feature.md` — agent groups, workflow
 * groups, etc.). Catalog Groups will EXTEND this user-groups primitive,
 * NOT replace it.
 */
export interface UserGroupRow {
  id: string;
  name: string;
  description: string | null;
  membershipType: 'manual' | 'dynamic';
  memberCount: number;
  createdBy: {
    id: string | null;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
  };
  createdAt: string;
  updatedAt: string;
}

interface UserGroupsListResponse {
  data: UserGroupRow[];
  pagination: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
}

export interface UseUserGroupsFilters {
  page?: number;
  pageSize?: number;
  search?: string;
}

/**
 * Fetch the user-group list for the caller's tenant. Optional `search`
 * filters on name + description (ILIKE).
 *
 * Query key: `['user-groups', filters]`. Mutations invalidate
 * `['user-groups']`. The dedicated `'user-groups'` key (not `'groups'`)
 * keeps Wave 5's Catalog Groups namespace open.
 */
export function useUserGroups(filters: UseUserGroupsFilters = {}): {
  groups: UserGroupRow[];
  pagination: UserGroupsListResponse['pagination'] | null;
  isLoading: boolean;
  error: unknown;
  refetch: () => void;
} {
  const params = new URLSearchParams();
  if (filters.page) params.set('page', String(filters.page));
  if (filters.pageSize) params.set('pageSize', String(filters.pageSize));
  if (filters.search) params.set('search', filters.search);
  const query = params.toString();
  const path = query ? `/users/groups?${query}` : '/users/groups';

  const { data, isLoading, error, refetch } = useQuery<UserGroupsListResponse>({
    queryKey: ['user-groups', filters],
    queryFn: () => apiClient.get<UserGroupsListResponse>(path),
    staleTime: 60_000,
  });

  return {
    groups: data?.data ?? [],
    pagination: data?.pagination ?? null,
    isLoading,
    error,
    refetch,
  };
}

/**
 * Body for `POST /api/v1/users/groups`. Server enforces:
 *   - `name`: 1-200 chars, trimmed
 *   - case-insensitive name uniqueness within tenant (409 NAME_CONFLICT)
 *   - `membershipType='dynamic'` rejected with 400 DYNAMIC_GROUPS_DEFERRED
 *     (F-W1a-09.1 — predicate-based dynamic groups land in v1.1)
 */
export interface CreateUserGroupBody {
  name: string;
  description?: string | null;
  membershipType?: 'manual' | 'dynamic';
}

/**
 * Create a user group — wires to W1a-09's POST /users/groups.
 */
export function useCreateUserGroup() {
  const qc = useQueryClient();
  return useMutation<UserGroupRow, Error, CreateUserGroupBody>({
    mutationFn: (body) => apiClient.post<UserGroupRow>('/users/groups', body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['user-groups'] });
    },
  });
}

/**
 * Body for `PATCH /api/v1/users/groups/:id`. All fields optional but at
 * least one must be present (else 422 VALIDATION_ERROR). Same name
 * uniqueness rules as POST.
 *
 * No-op short-circuit: if every field equals the existing value, the
 * server returns 200 with the existing row but emits NO new audit row.
 *
 * `membershipType` is intentionally absent — once 'manual', always
 * 'manual' in v1. v1.1's dynamic-groups feature will add a separate
 * transition flow.
 */
export interface UpdateUserGroupBody {
  name?: string;
  description?: string | null;
}

/**
 * Update a user group — wires to W1a-09's PATCH /users/groups/:id.
 */
export function useUpdateUserGroup() {
  const qc = useQueryClient();
  return useMutation<
    UserGroupRow,
    Error,
    { groupId: string; body: UpdateUserGroupBody }
  >({
    mutationFn: ({ groupId, body }) =>
      apiClient.patch<UserGroupRow>(`/users/groups/${groupId}`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['user-groups'] });
    },
  });
}

/**
 * Delete a user group — wires to W1a-09's DELETE /users/groups/:id.
 *
 * The `ON DELETE CASCADE` on `user_group_memberships(group_id)` cleanly
 * removes member rows; member-removal events are side-effects of the
 * group-deletion audit row (not separately audited). Any role grants
 * carried by the group (group_role_assignments — W1a-10) cascade too.
 */
export function useDeleteUserGroup() {
  const qc = useQueryClient();
  return useMutation<{ id: string; deleted: true }, Error, { groupId: string }>({
    mutationFn: ({ groupId }) =>
      apiClient.delete<{ id: string; deleted: true }>(
        `/users/groups/${groupId}`
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['user-groups'] });
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// W1a-10 — User Group detail (members + permissions)
// ─────────────────────────────────────────────────────────────────────────────
//
// Builds on W1a-09's group CRUD with three new endpoints:
//
//   GET    /api/v1/users/groups/:id                       — group + members[] + permissions[]
//   POST   /api/v1/users/groups/:id/members               — add members (idempotent)
//   DELETE /api/v1/users/groups/:id/members/:userId       — remove a member
//
// The detail endpoint returns the same shape as the W1a-09 list-row plus
// a `members[]` array (joined to the user's identity + their tenant role
// + the actor that added them) and a `permissions[]` placeholder. v1
// returns an empty `permissions[]`; the proper aggregation lands in
// W1a-12 / Wave 5 (F-W1a-10.1). The UI displays "Inherited from member
// roles — see individual user details" instead of attempting to render
// an empty array as the actual answer.

export interface UserGroupMember {
  userId: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  /**
   * The member's role in THIS tenant (from `user_organizations.role`),
   * not whatever roles they hold in other tenants. May be null if the
   * user-organizations row is missing (defensive — should not happen in
   * practice, but the LEFT JOIN tolerates it).
   */
  role: string | null;
  addedAt: string;
  addedBy: { id: string | null; name: string | null };
}

/**
 * Permission entry surfaced in the Permissions tab. v1 returns an empty
 * array; the wire shape is locked here so the UI can render the typed
 * empty state without changes when W1a-12 lights up the aggregation.
 */
export interface UserGroupPermission {
  action: string;
  resource: string;
  source: 'system_role' | 'custom_role' | 'group';
}

export interface UserGroupDetail extends UserGroupRow {
  members: UserGroupMember[];
  permissions: UserGroupPermission[];
}

/**
 * Fetch the group detail for the given id. Returns a 404 (surfaced as a
 * thrown error from `apiClient.get`) if the group doesn't exist OR
 * belongs to another tenant — the API never differentiates.
 *
 * Query key: `['user-groups', id]`. The W1a-10 mutations
 * (`useAddGroupMembers`, `useRemoveGroupMember`) invalidate both
 * `['user-groups', id]` and `['user-groups']` so the list-view
 * memberCount stays fresh.
 */
export function useUserGroup(id: string | undefined): {
  group: UserGroupDetail | null;
  isLoading: boolean;
  error: unknown;
  refetch: () => void;
} {
  const { data, isLoading, error, refetch } = useQuery<UserGroupDetail>({
    queryKey: ['user-groups', id],
    queryFn: () => apiClient.get<UserGroupDetail>(`/users/groups/${id}`),
    enabled: !!id,
    staleTime: 30_000,
  });

  return {
    group: data ?? null,
    isLoading,
    error,
    refetch,
  };
}

/**
 * Body for `POST /api/v1/users/groups/:id/members`.
 *
 * Server enforces:
 *   - userIds.length 1..200 (Zod min/max)
 *   - All userIds must be in the caller's tenant — cross-tenant ids
 *     fail the whole batch with 400 INVALID_MEMBERS + a `details.rejectedUserIds`.
 *   - ON CONFLICT DO NOTHING for already-members; the response
 *     partitions into `added` vs `alreadyMembers`.
 */
export interface AddGroupMembersBody {
  userIds: string[];
}

export interface AddGroupMembersResult {
  groupId: string;
  added: string[];
  alreadyMembers: string[];
  memberCount: number;
}

/**
 * Add members to a group — wires to W1a-10's POST /users/groups/:id/members.
 *
 * Idempotent: callers may re-send the same userIds; the response's
 * `alreadyMembers` array surfaces which ids were already in the group
 * so the UI can show "5 added, 2 already members".
 */
export function useAddGroupMembers() {
  const qc = useQueryClient();
  return useMutation<
    AddGroupMembersResult,
    Error,
    { groupId: string; body: AddGroupMembersBody }
  >({
    mutationFn: ({ groupId, body }) =>
      apiClient.post<AddGroupMembersResult>(
        `/users/groups/${groupId}/members`,
        body,
      ),
    onSuccess: (_data, vars) => {
      // Invalidate both the detail (so the members table refreshes) and
      // the list (so memberCount on the list view updates).
      qc.invalidateQueries({ queryKey: ['user-groups', vars.groupId] });
      qc.invalidateQueries({ queryKey: ['user-groups'] });
    },
  });
}

/**
 * Remove a single member from a group — wires to W1a-10's
 * DELETE /users/groups/:id/members/:userId.
 *
 * Idempotent: deleting a non-existent membership returns 200 (the
 * caller's intent — "this user shouldn't be in the group" — is
 * satisfied either way). Only cross-tenant userIds 404.
 */
export function useRemoveGroupMember() {
  const qc = useQueryClient();
  return useMutation<
    { groupId: string; userId: string; memberCount: number },
    Error,
    { groupId: string; userId: string }
  >({
    mutationFn: ({ groupId, userId }) =>
      apiClient.delete<{
        groupId: string;
        userId: string;
        memberCount: number;
      }>(`/users/groups/${groupId}/members/${userId}`),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['user-groups', vars.groupId] });
      qc.invalidateQueries({ queryKey: ['user-groups'] });
    },
  });
}
